import { DESKTOP_CHUNK_INTERVAL_DEFAULT_SECS } from '@/hooks/mutation/useMutateStartTauriRecording.constants';

/** Re-export the session start contract default (HAR-1327 / PostHog `desktop-chunk-size`). */
export { DESKTOP_CHUNK_INTERVAL_DEFAULT_SECS as DEFAULT_DESKTOP_CHUNK_INTERVAL_SECS };

/**
 * When the FE timer exceeds the last Rust segment by more than this ratio,
 * treat the session as multi-segment (pause/resume) and compare chunk count
 * against the segment duration only — not accumulated wall-clock (APP-6013).
 */
export const PAUSE_RESUME_SEGMENT_TOLERANCE_RATIO = 1.5;

/**
 * Post-resume segments must run at least this many chunk intervals before
 * ≤1 uploaded chunk is treated as truncation — mirrors the standard path's
 * `interval × 3` conservatism so healthy ~46–134s resumes with one chunk
 * stay safe at the 45s default cadence.
 */
export const POST_RESUME_MIN_INTERVALS_BEFORE_SINGLE_CHUNK_TRUNCATION = 3;

/**
 * Detect when captured audio duration far exceeds what the
 * uploaded chunk count could plausibly represent — the signature of a zombie
 * UI timer with a dead capture pipeline (e.g. one ~3 s chunk after 55 min).
 *
 * Conservative: only flags when the comparison window ran long enough to
 * expect several chunks and the upload count is a small fraction of that
 * expectation.
 */
export function isCaptureTruncatedVsWallClock({
  wallClockSecs,
  segmentDurationMs,
  totalChunks,
  chunkIntervalSecs = DESKTOP_CHUNK_INTERVAL_DEFAULT_SECS,
}: {
  wallClockSecs: number;
  /** Last Rust segment duration; when set, used for pause/resume-safe comparison. */
  segmentDurationMs?: number;
  totalChunks: number;
  chunkIntervalSecs?: number;
}): boolean {
  if (!Number.isFinite(wallClockSecs) || wallClockSecs <= 0) {
    return false;
  }
  if (!Number.isFinite(totalChunks) || totalChunks < 0) {
    return false;
  }

  const segmentSecs =
    segmentDurationMs != null &&
    Number.isFinite(segmentDurationMs) &&
    segmentDurationMs > 0
      ? segmentDurationMs / 1000
      : null;

  const interval = Math.max(1, chunkIntervalSecs);

  const isMultiSegment =
    segmentSecs != null &&
    wallClockSecs > segmentSecs * PAUSE_RESUME_SEGMENT_TOLERANCE_RATIO;

  // Post-resume short segment: when pause/resume collapses
  // compareSecs to the last segment, segments under interval×3 never reach
  // the chunk-ratio test — but ≤1 chunk after several full intervals on a
  // resumed segment still signals capture died early. Segments shorter than
  // interval×3 stay inconclusive (healthy single-chunk resumes in the
  // ~46–134s window at the 45s default).
  if (isMultiSegment && segmentSecs != null) {
    const minSegmentForSingleChunkFlag =
      interval * POST_RESUME_MIN_INTERVALS_BEFORE_SINGLE_CHUNK_TRUNCATION;
    if (segmentSecs >= minSegmentForSingleChunkFlag && totalChunks <= 1) {
      return true;
    }
  }

  const compareSecs = isMultiSegment ? segmentSecs : (segmentSecs ?? wallClockSecs);

  // Too short to infer truncation — a single chunk covers the whole window.
  if (compareSecs == null || compareSecs < interval * 3) {
    return false;
  }
  const expectedMinChunks = Math.floor((compareSecs - interval) / interval);
  if (expectedMinChunks < 2) {
    return false;
  }
  // Missing most of the expected chunks (e.g. 1 uploaded when ~70 were due).
  return totalChunks < Math.max(1, Math.floor(expectedMinChunks * 0.25));
}
